import React from 'react';
import { Form, Field } from 'react-final-form';

import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/form/button';
import { Image } from '@/shared/ui/image';
import { Condition } from '@/shared/ui/condition';
import { FormField } from '@/shared/ui/form/form-field';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';
import { Link } from '@/shared/ui/link';
import {
  BodyTextBoldSecondary,
  BoldHeadingSenary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';

import { useAuth } from '@/features/auth-managment/model/auth-context';
import {
  FormWrapper,
  FormTypeSection,
  ResetPasswordBlock,
  ButtonContinue,
  FormTypeSections,
  FormContainer,
  FormChangeSections,
} from '@/features/auth-managment/styles';
import { useRouter } from 'next/navigation';

export const Authorization = () => {
  const {
    authenticationType,
    authenticationMethod,
    setAuthenticationType,
    setAuthenticationMethod,
    onSubmit,
    form,
    spinner,
    handleResetPassword,
    t,
    PhoneFieldCmp,
    validate,
    handleClose,
    currentStep,
  } = useAuth();

  const router = useRouter();

  return (
    <FormWrapper $currentStep={currentStep}>
      <FormTypeSections>
        <FormTypeSection
          $type="signin"
          onClick={() => {
            setAuthenticationType('signin');
            router.push('/?modal=auth');
          }}
          $isActive={authenticationType === 'signin'}
        >
          <BoldHeadingSenary
            color={authenticationType === 'signin' ? '#FFF' : '#AFAFAF'}
          >
            {t('auth.sign_in')}
          </BoldHeadingSenary>
        </FormTypeSection>
        <FormTypeSection
          $type="signup"
          onClick={() => {
            setAuthenticationType('signup');
            router.push('/?modal=registration');
          }}
          $isActive={authenticationType === 'signup'}
        >
          <BoldHeadingSenary
            color={authenticationType === 'signup' ? '#FFF' : '#AFAFAF'}
          >
            {t('auth.sign_up')}
          </BoldHeadingSenary>
        </FormTypeSection>
        <FormTypeSection style={{ padding: '12px 16px' }} $type="close">
          <Box onClick={handleClose} $display="flex">
            <Image
              width={32}
              height={32}
              src="/images/icons/close-dialog.svg"
            />
          </Box>
        </FormTypeSection>
      </FormTypeSections>
      <FormContainer
        $withBackground={authenticationType === 'signup'}
        $isSignIn={authenticationType === 'signin'}
      >
        <FormChangeSections>
          <Button
            onClick={() => setAuthenticationMethod('email')}
            icon={
              <Image
                width={20}
                height={20}
                src="/images/icons/email-icon.svg"
              />
            }
            variant={
              authenticationMethod === 'email'
                ? 'orangeGradient'
                : 'transparent'
            }
          >
            <BodyTextBoldSecondary>
              {t('auth.login.email.button')}
            </BodyTextBoldSecondary>
          </Button>
          <Button
            onClick={() => setAuthenticationMethod('phone')}
            icon={
              <Image
                width={20}
                height={20}
                src="/images/icons/phone-icon.svg"
              />
            }
            variant={
              authenticationMethod === 'phone'
                ? 'orangeGradient'
                : 'transparent'
            }
          >
            <BodyTextBoldSecondary>
              {t('auth.login.phone.button')}
            </BodyTextBoldSecondary>
          </Button>
        </FormChangeSections>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          initialValues={form}
          render={({ handleSubmit, submitting, pristine, ...rest }) => (
            <form onSubmit={handleSubmit}>
              <Box $paddingTop={16}>
                <Condition match={authenticationMethod === 'email'}>
                  <Field
                    name={'email'}
                    autoComplete="off"
                    required
                    placeholder={t('auth.login.email.placeholder')}
                    component={FormField}
                    type={'text'}
                    label={
                      <BodyTextBoldSecondary>
                        {t('auth.login.email.label')}
                      </BodyTextBoldSecondary>
                    }
                  />
                </Condition>
                <Condition match={authenticationMethod === 'phone'}>
                  <Field
                    name={'phone'}
                    required
                    autoComplete="off"
                    component={PhoneFieldCmp}
                    type={'text'}
                    label={
                      <BodyTextBoldSecondary>
                        {t('auth.login.phone.label')}
                      </BodyTextBoldSecondary>
                    }
                  />
                </Condition>
              </Box>

              <Box $paddingTop={16}>
                <Field
                  name="password"
                  id="auth-email-password"
                  resetPasswordSlot={
                    authenticationType === 'signin' ? (
                      <ResetPasswordBlock onClick={handleResetPassword}>
                        {t('auth.login.forgot_password')}
                      </ResetPasswordBlock>
                    ) : null
                  }
                  component={FormField}
                  required
                  type="password"
                  placeholder={t('auth.login.password.placeholder')}
                  label={
                    <BodyTextBoldSecondary>
                      {t('auth.login.password.label')}
                    </BodyTextBoldSecondary>
                  }
                />
              </Box>

              <Condition match={authenticationType === 'signup'}>
                <Box $paddingTop={24}>
                  <Field
                    name="isLegalAge"
                    component={CheckboxField}
                    type="checkbox"
                    label={
                      <BodyTextRegularSecondary>
                        {t('auth.login.legal_age_checkbox')}
                      </BodyTextRegularSecondary>
                    }
                  />
                </Box>
                <Box $paddingTop={12}>
                  <Field
                    name="isTerms"
                    component={CheckboxField}
                    type="checkbox"
                    label={
                      <BodyTextRegularSecondary>
                        {t('auth.login.accept_checkbox', {
                          terms: (
                            <Link target="_blank" href="/terms">
                              <span style={{ color: '#FF6601' }}>
                                {t('auth.login.terms')}
                              </span>
                            </Link>
                          ),
                        })}
                      </BodyTextRegularSecondary>
                    }
                  />
                </Box>
                <Box $paddingTop={12}>
                  <Field
                    name="isNews"
                    component={CheckboxField}
                    type="checkbox"
                    label={
                      <BodyTextRegularSecondary>
                        {t('auth.login.news_checkbox')}
                      </BodyTextRegularSecondary>
                    }
                  />
                </Box>
              </Condition>
              <Box $paddingTop={24}>
                <ButtonContinue
                  type="submit"
                  disabled={rest.invalid}
                  isLoading={spinner}
                >
                  <BodyTextBoldSecondary>
                    {t('auth.login.submit')}
                  </BodyTextBoldSecondary>
                </ButtonContinue>
              </Box>
            </form>
          )}
        />
      </FormContainer>
    </FormWrapper>
  );
};
